enum EnvironmentName {
  Development = "dev",
  Staging = "staging",
  Production = "prod",
  Local = "local"
}

interface FeatureFlags {
  allowOutlookIntegration: boolean;
  allowSageIntegration: boolean;
  allowXeroIntegration: boolean;
  allowQuickbooksIntegration: boolean;
  allowFreeAgentIntegration: boolean;
  allowYapilyIntegration: boolean;
  blockComponentForProduction: boolean;
  allowCommunicationsHub: boolean;
}

interface EnvironmentConfig {
  apiEndpoint: string;
  baseUrl: string;
  authUrl: string;
  webhookUrl: string;
  sentryDsn: string;
  featureFlags: FeatureFlags;
}

const hostEnvironments: Record<string, EnvironmentName> = {
  localhost: EnvironmentName.Local,
  "fera.app.dev.jenesysdev.com": EnvironmentName.Development,
  "app.staging.jenesysdev.com": EnvironmentName.Staging,
  "jack.jenesys.co": EnvironmentName.Production
};

const environments: Record<EnvironmentName, EnvironmentConfig> = {
  [EnvironmentName.Local]: {
    apiEndpoint: "http://localhost:8000/v2",
    baseUrl: "http://localhost:3000",
    authUrl: "https://07550248.propelauthtest.com",
    webhookUrl: "http://localhost:8000",
    sentryDsn: "",
    featureFlags: {
      allowOutlookIntegration: true,
      allowSageIntegration: true,
      allowXeroIntegration: true,
      allowQuickbooksIntegration: true,
      allowFreeAgentIntegration: true,
      allowYapilyIntegration: true,
      blockComponentForProduction: false,
      allowCommunicationsHub: true
    }
  },
  [EnvironmentName.Development]: {
    apiEndpoint: "https://api.dev.jenesysdev.com/v2",
    baseUrl: "https://fera.app.dev.jenesysdev.com",
    authUrl: "https://07550248.propelauthtest.com",
    webhookUrl: "https://api.dev.jenesysdev.com",
    sentryDsn:
      "https://31c3a9b7a4054db49bbfe0b3b3063fb5@o4503959486259200.ingest.sentry.io/4505816893030400",
    featureFlags: {
      allowOutlookIntegration: true,
      allowSageIntegration: true,
      allowXeroIntegration: true,
      allowQuickbooksIntegration: true,
      allowFreeAgentIntegration: true,
      allowYapilyIntegration: true,
      blockComponentForProduction: false,
      allowCommunicationsHub: true
    }
  },
  [EnvironmentName.Staging]: {
    apiEndpoint: "https://api.staging.jenesysdev.com/v2",
    baseUrl: "https://fera.app.staging.jenesysdev.com",
    authUrl: "https://auth.app.staging.jenesysdev.com",
    webhookUrl: "https://api.staging.jenesysdev.com",
    sentryDsn:
      "https://31c3a9b7a4054db49bbfe0b3b3063fb5@o4503959486259200.ingest.sentry.io/4505816893030400",
    featureFlags: {
      allowOutlookIntegration: true,
      allowSageIntegration: true,
      allowXeroIntegration: true,
      allowQuickbooksIntegration: true,
      allowFreeAgentIntegration: true,
      allowYapilyIntegration: true,
      blockComponentForProduction: true,
      allowCommunicationsHub: true
    }
  },
  [EnvironmentName.Production]: {
    apiEndpoint: "https://api.jenesys.co/v2",
    baseUrl: "https://jack.jenesys.co",
    authUrl: "https://auth.jenesys.co",
    webhookUrl: "https://api.jenesys.co",
    sentryDsn:
      "https://31c3a9b7a4054db49bbfe0b3b3063fb5@o4503959486259200.ingest.sentry.io/4505816893030400",
    featureFlags: {
      allowOutlookIntegration: true,
      allowSageIntegration: true,
      allowXeroIntegration: true,
      allowQuickbooksIntegration: true,
      allowFreeAgentIntegration: true,
      allowYapilyIntegration: true,
      blockComponentForProduction: true,
      allowCommunicationsHub: true
    }
  }
};

const validateEnvironmentName = (
  env: string | undefined
): EnvironmentName | undefined =>
  Object.values(EnvironmentName).includes(env as EnvironmentName)
    ? (env as EnvironmentName)
    : undefined;

const getOverrideEnvironment = (): EnvironmentName | undefined => {
  // Check for environment override from command line
  const envOverride = validateEnvironmentName(
    import.meta.env.VITE_OVERRIDE_ENV
  );
  if (envOverride) {
    console.log(`Environment override detected: ${envOverride}`);
    return envOverride;
  }
  return undefined;
};

export const getEnvironmentName = (): EnvironmentName | undefined => {
  // First check for environment override
  const overrideEnv = getOverrideEnvironment();
  if (overrideEnv) {
    return overrideEnv;
  }

  // Fall back to existing hostname-based detection
  return (
    hostEnvironments[window.location.hostname] ??
    validateEnvironmentName(import.meta.env.VITE_BASE_APP_ENV)
  );
};

export const isEnvironment = (environmentName: EnvironmentName): boolean =>
  getEnvironmentName() === environmentName;

export const isDevelopment = (): boolean =>
  isEnvironment(EnvironmentName.Development);
export const isStaging = (): boolean => isEnvironment(EnvironmentName.Staging);

export const isProduction = (): boolean =>
  isEnvironment(EnvironmentName.Production);

export const isLocal = (): boolean => isEnvironment(EnvironmentName.Local);

export const isLocalOrDevelopment = (): boolean => isLocal() || isDevelopment();

export const isProductionOrStaging = (): boolean =>
  isProduction() || isStaging();

export const isSentryEnabled = (): boolean => !isLocal();

// Helper functions to access feature flags
export const getFeatureFlags = (): FeatureFlags => {
  const config =
    environments[getEnvironmentName() ?? EnvironmentName.Development];
  return config.featureFlags;
};

export const isFeatureEnabled = (featureName: keyof FeatureFlags): boolean =>
  getFeatureFlags()[featureName];

// Helper functions to access other environment values
export const getAuthUrl = (): string => {
  const config =
    environments[getEnvironmentName() ?? EnvironmentName.Development];
  return config.authUrl;
};

export const getWebhookUrl = (): string => {
  const config =
    environments[getEnvironmentName() ?? EnvironmentName.Development];
  return config.webhookUrl;
};

export const getSentryDsn = (): string => {
  const config =
    environments[getEnvironmentName() ?? EnvironmentName.Development];
  return config.sentryDsn;
};

// Export config for the current environment, which defaults to dev.
export default environments[
  getEnvironmentName() ?? EnvironmentName.Development
];
